import React, { useState } from 'react';
import { Mail, MessageSquare, User, Send } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const { currentTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: 'custom-beat',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thanks for reaching out. We'll get back to you within 24 hours.",
    });
    setFormData({ name: '', email: '', projectType: 'custom-beat', message: '' });
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Get In Touch</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Ready to create something special? Let's discuss your project and bring your vision to life.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Send a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-gray-400 text-sm mb-2 block">
                  <User className="w-4 h-4 inline mr-2" />
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                  style={{ '--tw-ring-color': currentTheme.primary }}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                  style={{ '--tw-ring-color': currentTheme.primary }}
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">Project Type</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white focus:outline-none focus:ring-2 transition-all"
                  style={{ '--tw-ring-color': currentTheme.primary }}
                >
                  <option value="custom-beat">Custom Beat</option>
                  <option value="exclusive-rights">Exclusive Rights</option>
                  <option value="collaboration">Collaboration</option>
                  <option value="mixing-mastering">Mixing & Mastering</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-gray-400 text-sm mb-2 block">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Project Details
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all resize-none"
                  style={{ '--tw-ring-color': currentTheme.primary }}
                  placeholder="Tell us about your project, vision, and any specific requirements..."
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-black font-bold text-lg hover:scale-105 transition-transform"
                style={{ backgroundColor: currentTheme.primary }}
              >
                <Send className="w-5 h-5 mr-2" />
                Send Message
              </Button>
            </form>
          </div>

          {/* Info Section */}
          <div className="space-y-8">
            {/* Service Cards */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6">Services Offered</h3>
              <ul className="space-y-4">
                {[
                  'Custom Beat Production',
                  'Exclusive Rights & Licensing',
                  'Artist Collaboration',
                  'Mixing & Mastering',
                  'Sound Design',
                  'EP/Album Production'
                ].map((service, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                         style={{ backgroundColor: `${currentTheme.primary}30` }}>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: currentTheme.primary }} />
                    </div>
                    <span className="text-gray-300">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Response Time */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-4">Quick Response</h3>
              <p className="text-gray-400 mb-4">
                I typically respond to all inquiries within 24 hours. For urgent requests, please mention it in your message.
              </p>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: currentTheme.primary }} />
                <span className="text-gray-400 text-sm">Usually replies within hours</span>
              </div>
            </div>

            {/* Social Proof */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-4">Why Work With Me?</h3>
              <div className="space-y-3 text-gray-400">
                <p>✓ 20 years of production experience</p>
                <p>✓ Worked with 50+ artists and growing</p>
                <p>✓ Fast turnaround times</p>
                <p>✓ Professional quality guaranteed</p>
                <p>✓ Revisions included</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;